import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';

import { useIsMobile } from '@/app/useIsMobile';
import { useStaffAuth } from '@/features/auth/useStaffAuth';
import { Pill, SECONDARY_BUTTON, formatInstant } from '@/features/inventory/registerUi';
import { useTenants } from '@/features/tenants/useTenants';
import {
  useWhatsAppMessages,
  type WhatsAppFilters,
  type WhatsAppMessage,
} from '@/features/whatsapp/useWhatsAppMessages';

/**
 * THE WHATSAPP LOG (slice 70b) — every message to and from customers.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 READ-ONLY LIST. There is no compose/reply/resend affordance ON THIS SCREEN —
 * the list only reads. Replying happens on the per-thread drill-down
 * (`/whatsapp/c/:conversationId`, slice 70c), reached by the "Open" link on each
 * row; a reply is sent there, inside the 24 h window, not from the log.
 *
 * 🔴 SUPER-ADMIN ONLY — AND THE GATE IS HERE, NOT ONLY IN THE NAV. The API 404s a
 * non-super-admin (`requireSuperAdminOr404`), the nav hides the entry for them,
 * and this component redirects a non-super-admin to the tenants list so a deep
 * link cannot dangle a screen whose every fetch 404s. Modelled on `Staff.tsx` and
 * `Vehicles.tsx`.
 *
 * 🔴 THE FULL PHONE IS NEVER ON THE WIRE. The API masks to the last 3 digits
 * (`***499`); this screen renders exactly what it is given and can un-mask
 * nothing.
 */

/** Status-pill tone. Green (the STATUS token, never brand) marks delivered/read. */
const STATUS_TONE: Record<string, string> = {
  pending: 'border-raad-border text-raad-muted',
  sent: 'border-raad-primary/50 text-raad-accent',
  delivered: 'border-raad-marker-moving/40 text-raad-marker-moving',
  read: 'border-raad-marker-moving/40 text-raad-marker-moving',
  failed: 'border-raad-error/40 text-raad-error',
  received: 'border-raad-primary/50 text-raad-accent',
};

const DIRECTION_TONE: Record<string, string> = {
  inbound: 'border-raad-primary/50 text-raad-accent',
  outbound: 'border-raad-border text-raad-muted',
};

// ── Shared cell renderers — ONE source for the table (md+) and the card (< md) ──

function StatusCell({ m }: { m: WhatsAppMessage }) {
  const { t, i18n } = useTranslation();
  const tone = STATUS_TONE[m.status] ?? 'border-raad-border text-raad-muted';
  // The ladder: only the rungs that actually happened, each with its instant.
  const rungs: Array<[string, string | null]> = [
    ['sent', m.sentAt],
    ['delivered', m.deliveredAt],
    ['read', m.readAt],
  ];
  return (
    <div className="space-y-1">
      <Pill label={t(`whatsapp.status.${m.status}`, m.status)} tone={tone} />
      {m.status === 'failed' && m.errorTitle ? (
        <p className="text-[11.5px] text-raad-error">
          {m.errorTitle}
          {m.errorCode ? <span className="text-raad-muted"> ({m.errorCode})</span> : null}
        </p>
      ) : (
        rungs
          .filter(([, at]) => at !== null)
          .map(([rung, at]) => (
            <p key={rung} className="text-[11px] text-raad-muted">
              {t(`whatsapp.status.${rung}`)}
              {' · '}
              <span className="num">{formatInstant(at as string, i18n.language)}</span>
            </p>
          ))
      )}
    </div>
  );
}

/** Template (outbound) above the customer's reply text (inbound), or an em-dash. */
function MessageCell({ m }: { m: WhatsAppMessage }) {
  const { t } = useTranslation();
  if (m.templateName === null && (m.bodyText === null || m.bodyText === '')) {
    return (
      <span className="text-raad-muted" aria-hidden>
        &mdash;
      </span>
    );
  }
  return (
    <div className="space-y-1">
      {m.templateName !== null ? (
        <p className="text-[12.5px]">
          <span className="text-raad-muted">{t('whatsapp.templateLabel')}: </span>
          <span className="num">{m.templateName}</span>
        </p>
      ) : null}
      {/* 🔴 Plain React text, NEVER dangerouslySetInnerHTML — this is customer-typed
          content. React escapes it; that is the whole protection. */}
      {m.bodyText !== null && m.bodyText !== '' ? (
        <p className="whitespace-pre-wrap break-words text-[12.5px] text-raad-text">{m.bodyText}</p>
      ) : null}
    </div>
  );
}

function DirectionPill({ direction }: { direction: string }) {
  const { t } = useTranslation();
  const tone = DIRECTION_TONE[direction] ?? 'border-raad-border text-raad-muted';
  return <Pill label={t(`whatsapp.direction.${direction}`, direction)} tone={tone} />;
}

/**
 * To/From. 🔴 For an unattributed inbound ("Unknown sender") the API sends the FULL
 * number (`peerPhone`) and we show it — Mahad needs to see who it was. For every
 * attributed row only the mask (`***499`) is on the wire, so that is all we can show.
 */
function PhoneCell({ m }: { m: WhatsAppMessage }) {
  return <span className="num break-all text-raad-muted">{m.peerPhone ?? m.peerPhoneMasked}</span>;
}

function CompanyName({ m }: { m: WhatsAppMessage }) {
  const { t } = useTranslation();
  return (
    <span className={m.tenantName === null ? 'text-raad-muted' : 'text-raad-text'}>
      {/* 🔴 A null tenant is an UNATTRIBUTED inbound (no context.id, no recent
          outbound) — show "Unknown sender", NEVER a company name guessed from a
          user's phone (the 31 Oct misattribution the API fix removed). */}
      {m.tenantName ?? t('whatsapp.unknownSender')}
    </span>
  );
}

/** The mobile card (< md). Same data, same cell renderers as the table row. */
function MessageCard({ m }: { m: WhatsAppMessage }) {
  const { t, i18n } = useTranslation();
  return (
    <div
      data-testid="whatsapp-card"
      className="rounded-panel border border-raad-border bg-raad-panel p-4"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <DirectionPill direction={m.direction} />
        <span className="num text-[12.5px]">
          <PhoneCell m={m} />
        </span>
        <span className="ms-auto text-[11.5px] text-raad-muted num">
          {formatInstant(m.createdAt, i18n.language)}
        </span>
      </div>
      <div className="mt-2 text-[12.5px]">
        <CompanyName m={m} />
      </div>
      <div className="mt-3 grid gap-3">
        <div>
          <dt className="text-[10.5px] uppercase tracking-wide text-raad-muted">
            {t('whatsapp.col.message')}
          </dt>
          <dd className="mt-0.5">
            <MessageCell m={m} />
          </dd>
        </div>
        <div>
          <dt className="text-[10.5px] uppercase tracking-wide text-raad-muted">
            {t('whatsapp.col.status')}
          </dt>
          <dd className="mt-0.5">
            <StatusCell m={m} />
          </dd>
        </div>
      </div>
      <div className="mt-3">
        <Link
          to={`/whatsapp/c/${m.conversationId}`}
          className="text-[12.5px] font-semibold text-raad-accent"
        >
          {t('whatsapp.openThread')}
        </Link>
      </div>
    </div>
  );
}

export function WhatsApp() {
  const { t } = useTranslation();
  const { state } = useStaffAuth();
  const isMobile = useIsMobile();

  const [tenantId, setTenantId] = useState('');
  const [status, setStatus] = useState('');
  const [direction, setDirection] = useState('');
  const [search, setSearch] = useState('');

  const filters: WhatsAppFilters = useMemo(() => {
    const f: WhatsAppFilters = { limit: 50 };
    if (tenantId !== '') f.tenantId = tenantId;
    if (status !== '') f.status = status;
    if (direction !== '') f.direction = direction;
    return f;
  }, [tenantId, status, direction]);

  const q = useWhatsAppMessages(filters);
  const tenantsQ = useTenants();

  const rows: WhatsAppMessage[] = useMemo(
    () => (q.data?.pages ?? []).flatMap((p) => p.messages),
    [q.data],
  );

  const tenantOptions = useMemo(() => {
    const list = tenantsQ.data?.tenants ?? [];
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [tenantsQ.data]);

  const needle = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      needle === ''
        ? rows
        : rows.filter((m) =>
            [m.templateName, m.peerPhoneMasked, m.bodyText].some(
              (x) => typeof x === 'string' && x.toLowerCase().includes(needle),
            ),
          ),
    [rows, needle],
  );

  // 🔴 The route-level gate, AFTER every hook (hooks must run unconditionally).
  // RequireStaff guarantees we are authenticated, so any non-super-admin here is
  // an admin_staff who must not see this (the API 404s them). Redirect rather than
  // render a screen whose every fetch 404s.
  if (state.status === 'authenticated' && state.user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }

  const STATUSES = ['pending', 'sent', 'delivered', 'read', 'failed', 'received'] as const;

  const Header = (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
      <h1 id="whatsapp-heading" className="font-display text-xl font-bold tracking-tight">
        {t('whatsapp.title')}
      </h1>
      <p className="text-sm text-raad-muted">{t('whatsapp.subtitle')}</p>
    </div>
  );

  if (q.isPending) {
    return (
      <section aria-labelledby="whatsapp-heading">
        {Header}
        <div className="mt-4 grid gap-3" role="status" aria-live="polite">
          <span className="sr-only">{t('whatsapp.loading')}</span>
          <div className="h-40 animate-pulse rounded-panel border border-raad-border bg-raad-panel" />
        </div>
      </section>
    );
  }

  if (q.isError) {
    return (
      <section aria-labelledby="whatsapp-heading">
        {Header}
        <p role="alert" className="mt-4 text-sm text-raad-error">
          {t('whatsapp.error')}
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="whatsapp-heading">
      {Header}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label className="block max-w-xs grow">
          <span className="sr-only">{t('whatsapp.search')}</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('whatsapp.search')}
            className="min-h-11 sm:min-h-10 w-full rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          />
        </label>
        <label className="block">
          <span className="sr-only">{t('whatsapp.filterTenant')}</span>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="min-h-11 sm:min-h-10 rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          >
            <option value="">{t('whatsapp.allTenants')}</option>
            {tenantOptions.map((tnt) => (
              <option key={tnt.id} value={tnt.id}>
                {tnt.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="sr-only">{t('whatsapp.filterDirection')}</span>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            className="min-h-11 sm:min-h-10 rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          >
            <option value="">{t('whatsapp.anyDirection')}</option>
            <option value="outbound">{t('whatsapp.direction.outbound')}</option>
            <option value="inbound">{t('whatsapp.direction.inbound')}</option>
          </select>
        </label>
        <label className="block">
          <span className="sr-only">{t('whatsapp.filterStatus')}</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="min-h-11 sm:min-h-10 rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          >
            <option value="">{t('whatsapp.anyStatus')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`whatsapp.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{t('whatsapp.empty')}</p>
      ) : filtered.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{t('whatsapp.noMatch')}</p>
      ) : isMobile ? (
        <div className="mt-4 space-y-3">
          {filtered.map((m) => (
            <MessageCard key={m.id} m={m} />
          ))}
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-panel border border-raad-border bg-raad-panel px-4">
          <table className="w-full min-w-[52rem] text-sm">
            <thead>
              <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('whatsapp.col.time')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('whatsapp.col.company')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('whatsapp.col.direction')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('whatsapp.col.phone')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('whatsapp.col.message')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('whatsapp.col.status')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  <span className="sr-only">{t('whatsapp.openThread')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <WhatsAppRow key={m.id} m={m} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* "Load more" — present ONLY while the server says there is another page.
          A client-side search narrows the LOADED rows; it never hides this, so a
          match further back can still be paged in. */}
      {q.hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            className={SECONDARY_BUTTON}
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            {q.isFetchingNextPage ? t('whatsapp.loadingMore') : t('whatsapp.loadMore')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function WhatsAppRow({ m }: { m: WhatsAppMessage }) {
  const { t, i18n } = useTranslation();
  return (
    <tr className="border-b border-raad-border last:border-b-0">
      <td className="py-3 pr-4 align-top">
        <span className="num text-[12px]">{formatInstant(m.createdAt, i18n.language)}</span>
      </td>
      <td className="py-3 pr-4 align-top">
        <CompanyName m={m} />
      </td>
      <td className="py-3 pr-4 align-top">
        <DirectionPill direction={m.direction} />
      </td>
      <td className="py-3 pr-4 align-top">
        <PhoneCell m={m} />
      </td>
      <td className="py-3 pr-4 align-top">
        <MessageCell m={m} />
      </td>
      <td className="py-3 pr-4 align-top">
        <StatusCell m={m} />
      </td>
      <td className="py-3 pr-4 align-top text-right">
        <Link to={`/whatsapp/c/${m.conversationId}`} className="text-[12.5px] font-semibold text-raad-accent">
          {t('whatsapp.openThread')}
        </Link>
      </td>
    </tr>
  );
}

export default WhatsApp;
